import { useEffect, useState } from "react";
import api, { formatApiError, formatMoney } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Truck } from "lucide-react";

const empty = { nombre: "", tipo: "ciudad", precio: 0, zona: "", activo: true };

const TIPOS = [
  { value: "ciudad", label: "Ciudad" },
  { value: "departamento", label: "Departamento" },
  { value: "expreso", label: "Cargo Expreso" },
];

export default function Envios() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const { data } = await api.get("/shipping");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...empty, ...s }); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form, precio: Number(form.precio) };
      if (editing) await api.put(`/shipping/${editing.id}`, payload);
      else await api.post("/shipping", payload);
      toast.success(editing ? "Envío actualizado" : "Envío creado");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (s) => {
    if (!window.confirm(`¿Eliminar "${s.nombre}"?`)) return;
    try { await api.delete(`/shipping/${s.id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="envios-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">Logística</div>
          <h1 className="font-manrope font-black tracking-tight text-4xl mt-1">Métodos de Envío</h1>
          <p className="text-zinc-500 mt-2 text-sm">Envíos por Ciudad, Departamento o Cargo Expreso con precio propio.</p>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} className="bg-[#002FA7] hover:bg-[#00227A] gap-2" data-testid="new-shipping-btn">
              <Plus className="w-4 h-4" /> Nuevo envío
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editing ? "Editar envío" : "Nuevo método de envío"}</DialogTitle>
              <DialogDescription>Se usará como un ítem con precio en los pedidos.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Nombre</Label>
                <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="shipping-nombre" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Tipo</Label>
                  <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                    <SelectTrigger data-testid="shipping-tipo"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TIPOS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Precio</Label>
                  <Input type="number" min={0} step="0.01" value={form.precio}
                         onChange={(e) => setForm({ ...form, precio: e.target.value })} data-testid="shipping-precio" />
                </div>
              </div>
              <div>
                <Label>Zona (opcional)</Label>
                <Input value={form.zona} onChange={(e) => setForm({ ...form, zona: e.target.value })} placeholder="Ej. Cochabamba, Zona Norte..." />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button onClick={save} className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="save-shipping-btn">Guardar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((s) => (
          <Card key={s.id} className="p-5 border-zinc-200 rounded-md hover:shadow-sm transition-transform duration-200 ease-out hover:-translate-y-px">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-md bg-zinc-900 text-white grid place-items-center">
                  <Truck className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{TIPOS.find(t => t.value === s.tipo)?.label || s.tipo}</div>
                  <div className="font-semibold">{s.nombre}</div>
                  {s.zona && <div className="text-xs text-zinc-500 mt-0.5">{s.zona}</div>}
                </div>
              </div>
              <div className="font-manrope font-black text-lg tracking-tight">{formatMoney(s.precio)}</div>
            </div>
            <div className="mt-4 flex gap-2">
              <Button size="sm" variant="outline" className="flex-1 gap-2" onClick={() => openEdit(s)}>
                <Pencil className="w-3.5 h-3.5" /> Editar
              </Button>
              <Button size="sm" variant="ghost" className="text-red-600" onClick={() => remove(s)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </Card>
        ))}
        {items.length === 0 && (
          <div className="col-span-full text-center text-zinc-400 text-sm py-12 border border-dashed rounded-md">
            Sin métodos de envío.
          </div>
        )}
      </div>
    </div>
  );
}
